/* Shopify owns discount validation. A shareable link carries the code into checkout. */
if (!customElements.get('cv-rav')) {
  customElements.define('cv-rav', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const form = this.querySelector('[data-code-form]');
      const input = this.querySelector('[data-code-input]');
      const status = this.querySelector('[data-code-status]');
      const copy = this.querySelector('[data-copy]');
      const find = this.querySelector('[data-find-safe]');
      const finder = this.querySelector('cv-finder');
      find.hidden = false;
      finder.addEventListener('change', () => { this.destination = null; }, { signal, capture: true });
      this.addEventListener('cv-finder:navigate', event => {
        event.preventDefault();
        this.destination = event.detail.destination;
        finder.querySelector('.cv-finder__status').textContent = 'Your vehicle is selected. Choose Find my safe.';
      }, { signal });
      find.addEventListener('click', () => {
        if (!this.destination) {
          const next = [...finder.querySelectorAll('.easysearch-field-select-holder select')].find(select => !select.value);
          const toggle = next?.parentElement.querySelector('.cv-finder__select-toggle');
          toggle?.focus();
          finder.querySelector('.cv-finder__status').textContent = next ? 'Choose your make, model and year first.' : 'Still finding your fit. Please try again, or open the vehicle finder below.';
          return;
        }
        if (!form) { window.location.assign(this.destination); return; }
        if (!input.value.trim()) { input.value = ''; input.reportValidity(); input.focus(); return; }
        const url = this.discountURL(input.value, this.destination);
        if (url) window.location.assign(url);
      }, { signal });
      this.querySelectorAll('[data-rav-make]').forEach(button => {
        button.addEventListener('click', () => {
          const select = finder.querySelector('.easysearch-field-select-holder select');
          const option = select && [...select.options].find(option => option.text.toLowerCase() === button.dataset.ravMake);
          if (!option || typeof finder.changeVehicle !== 'function') {
            finder.querySelector('.cv-finder__select-toggle')?.focus();
            return;
          }
          select.value = option.value;
          finder.changeVehicle(select);
          finder.sync();
          finder.querySelectorAll('.cv-finder__select-toggle')[1]?.focus();
        }, { signal });
      });
      if (!form) return;
      this.applyOffer(input.value.trim());
      copy.hidden = false;
      const apply = this.querySelector('[data-apply]');
      if (apply) apply.hidden = false;
      input.addEventListener('input', () => { status.textContent = ''; copy.textContent = 'Copy code'; }, { signal });
      form.addEventListener('submit', event => {
        event.preventDefault();
        input.value = input.value.trim();
        if (!form.reportValidity()) return;
        const url = this.discountURL(input.value, this.dataset.shopPath);
        if (url) window.location.assign(url);
      }, { signal });
      copy.addEventListener('click', async () => {
        const code = input.value.trim();
        if (!code) { input.value = ''; input.reportValidity(); return; }
        try {
          await navigator.clipboard.writeText(code);
          copy.textContent = 'Copied';
          status.textContent = 'Code copied. You can also enter it at checkout.';
        } catch (_) {
          input.focus(); input.select();
          status.textContent = 'Select and copy your code to use at checkout.';
        }
      }, { signal });
    }
    async applyOffer(code) {
      const status = this.querySelector('[data-activation-status]');
      const label = this.querySelector('[data-code-label]');
      const retry = this.querySelector('[data-activation-retry]');
      this.dataset.discountState = 'loading';
      status.textContent = 'Applying your discount…';
      const request = new AbortController();
      this.discountRequest = request;
      const timer = setTimeout(() => request.abort(), 10000);
      try {
        const options = { credentials: 'same-origin', signal: request.signal, cache: 'no-store' };
        const current = await fetch(this.dataset.cartPath + '.js', options);
        if (!current.ok) throw new Error('Cart unavailable');
        const cart = await current.json();
        const codes = [...new Set([...(cart.discount_codes || []).map(item => item.code), code])];
        const response = await fetch(this.dataset.cartPath + '/update.js', {
          ...options, method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') })
        });
        if (!response.ok) throw new Error('Discount unavailable');
        const result = await response.json();
        const state = this.offerState(result, code);
        this.dataset.discountState = state;
        if (state === 'applied') {
          label.textContent = '✓ Discount applied';
          status.textContent = `Your ${this.dataset.savings} discount is applied. You’re ready to shop.`;
        } else if (state === 'ready') {
          label.textContent = '✓ Code saved for checkout';
          status.textContent = `Your ${this.dataset.savings} offer is ready. Add your safe to see your savings—no code entry needed.`;
        } else {
          label.textContent = 'Your exclusive discount code';
          status.textContent = 'This code isn’t applying to your current cart. Check your items and other discounts at checkout.';
          retry.hidden = false;
        }
      } catch (_) {
        this.dataset.discountState = 'error';
        status.textContent = 'We couldn’t apply your code automatically. Use the link below or enter it at checkout.';
        retry.hidden = false;
      } finally {
        clearTimeout(timer);
      }
    }
    offerState(cart, code) {
      const discount = (cart.discount_codes || []).find(item => item.code.toUpperCase() === code.toUpperCase());
      if (discount?.applicable) return 'applied';
      if (discount && cart.item_count === 0) return 'ready';
      return 'unavailable';
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.discountRequest?.abort();
    }
    discountURL(code, destination) {
      const value = code.trim();
      if (!value || value.length > 100) return null;
      const target = new URL(destination, window.location.origin);
      if (target.origin !== window.location.origin) return null;
      const discount = new URL(this.dataset.discountPath + encodeURIComponent(value), window.location.origin);
      discount.searchParams.set('redirect', target.pathname + target.search + target.hash);
      return discount.pathname + discount.search;
    }
  });
}
